import React from 'react';
import { Dialog, DialogActions, DialogContent, Alert, AlertTitle } from '@mui/material';
import { StyledBodyText, StyledHeaderTextH2 } from '../../../../../common/Styleguide/Common/Text';
import { StyledButton } from '../../../../../common/Styleguide/Common/Button';
import { toast } from 'react-toastify';
import { GameJamLink, getGameJamLink } from './gamejamHelper';
import ArrowLeft from '../../../../../common/icons/ArrowLeft';
import { ContentCopy, OpenInNew, WarningAmber } from '@mui/icons-material';
import { GameJamId, getGameJamConfig } from '@crazygames/gamejam/game-jam-config';
import gamemakerLogo from '../../../../../common/img/202509_gamemaker_logo.svg';

interface GameJamConfirmationProps {
  submissionId: string;
  goToSubmission: () => void;
  gameJamId: GameJamId;
}

const GameJamConfirmation: React.FC<GameJamConfirmationProps> = ({ submissionId, gameJamId, goToSubmission }) => {
  const previewLink = getGameJamLink(submissionId);
  const gameJamConfig = getGameJamConfig(gameJamId);

  // Dynamic import of SVG is a mess, so we have to do this, we can remove finished GJ logos
  const getLogo = (id: GameJamId) => {
    switch (id) {
      case '202509_gamemaker':
        return gamemakerLogo;
      default:
        return undefined;
    }
  };

  if (!gameJamConfig) {
    return null;
  }

  const { link: gameJamPageLink, displayLink: gameJamLinkText, end, id } = gameJamConfig;
  const gameJamPreviewLink = getGameJamLink(submissionId);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(gameJamPreviewLink);
    toast.success('Link copied to clipboard');
  };

  return (
    <Dialog open maxWidth={'md'}>
      <DialogContent>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={getLogo(id)} style={{ height: 120 }} alt="Game Jam" />
        </div>
        <StyledHeaderTextH2>Thank You for Your Submission!</StyledHeaderTextH2>
        <StyledBodyText>
          Here is your shareable link:{' '}
          <GameJamLink href={previewLink} target="_blank">
            {previewLink}
          </GameJamLink>
        </StyledBodyText>
        <Alert icon={<WarningAmber sx={{ fontSize: 50 }} />} severity="warning" sx={{ fontSize: 18 }}>
          <AlertTitle color={'white'} sx={{ fontSize: 22 }}>
            Important next steps:
          </AlertTitle>
          <ul>
            <li>Test your game using the link above.</li>
            <li>
              Register your participation at{' '}
              <GameJamLink href={gameJamPageLink} target="_blank" sx={{ color: 'white' }}>
                {gameJamLinkText}
              </GameJamLink>{' '}
              using this shareable link.
            </li>
          </ul>
        </Alert>
        <StyledBodyText>
          What You Need to Know:
          <ul>
            <li>Your game is currently only accessible as a preview via this link.</li>
            <li>It cannot be discovered on CrazyGames and does not generate revenue at this stage.</li>
            <li>You can update your submission anytime until the game jam ends (updates may take a few minutes to appear).</li>
            <li>
              Submitting your game to CrazyGames is not enough - you must also register at{' '}
              <GameJamLink href={gameJamPageLink} target="_blank" sx={{ color: 'white' }}>
                {gameJamLinkText}
              </GameJamLink>
              .
            </li>
          </ul>
        </StyledBodyText>
        <StyledBodyText>
          Deadline Information:
          <ul>
            <li>Your preview link remains the same even after updates.</li>
            <li>
              To ensure validating your participation:
              <ul>
                <li>
                  Register your participation well before the deadline, you'll still be able to update your game here until last minute.
                </li>
                <li>Regularly update and test your game here.</li>
                <li>
                  Some Unity build options can considerably increase the build time, so don’t wait until the last minute to build your final
                  result.
                </li>
              </ul>
            </li>
            <li>You cannot update your game or register after the game jam ends.</li>
            <li>
              The deadline (
              <b>
                {new Date(end).toLocaleString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: false,
                  timeZone: 'CET',
                })}{' '}
                CET on {new Date(end).toLocaleString('en-US', { month: 'long', day: 'numeric' })}
              </b>
              ) is automatic and no exceptions will be made.
            </li>
          </ul>
        </StyledBodyText>
        <StyledBodyText>
          Note: Your game will not be published on CrazyGames immediately. However, our team is always on the lookout for great games. We
          will be reviewing all game jam submissions, and if we determine that your game has commercial potential, we will reach out with
          further instructions in the coming weeks.
        </StyledBodyText>
      </DialogContent>
      <DialogActions sx={{ py: 3 }}>
        <StyledButton onClick={goToSubmission} variant="contained" color="white" height={50} sx={{ minWidth: 200, mr: 1 }}>
          <ArrowLeft />
          Back to submission
        </StyledButton>
        <StyledButton onClick={handleCopyLink} variant="contained" color="white" height={50} sx={{ minWidth: 200 }}>
          <ContentCopy />
          Copy link
        </StyledButton>
        <a href={gameJamPreviewLink} target="_blank" rel="noreferrer">
          <StyledButton variant="contained" color="purple" height={50} sx={{ minWidth: 200 }}>
            <OpenInNew />
            Preview your game
          </StyledButton>
        </a>
      </DialogActions>
    </Dialog>
  );
};

export default GameJamConfirmation;
